import * as React from 'react';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { z } from 'zod';
import { energyLabelState, type TEnergyLabelSize } from 'state/energyLabel';
import { userSession } from 'state/session';
import { tenantConfig } from 'utils/tenant';
import { user } from 'utils/auth/user';
import { useExperienceSync } from 'utils/sync/useExperienceSync';
import { MinimizeIcon } from 'components/icons/MinimizeIcon';
import type { IVehicle } from 'data/schema';

const energyLabelSyncSchema = z.object({
  sizes: z.record(z.string(), z.enum(['minimized', 'maximized'])),
});

type IEnergyLabelSyncState = z.infer<typeof energyLabelSyncSchema>;

export interface IEnergyLabelProps {
  vehicleId: string;
  imageUrl: string | null;
}

function useEnergyLabelGating(vehicleId: string, imageUrl: string | null) {
  const session = userSession().useState();
  const labelState = energyLabelState().useState();
  tenantConfig().useState();

  const isHost = user.isDealer;
  const isFlagEnabled = tenantConfig().isAllowed('energyLabel');
  const isCustomerBlocked = !isHost && !session.customerAllowed;
  const isVisible = isFlagEnabled && !!imageUrl && !labelState.fallbackActive && !isCustomerBlocked;

  return { isVisible, isHost, session, labelState };
}

/**
 * Note for anyone extending IEnergyLabelSyncState (AVS-4401): sends now carry the FULL state, not
 * a delta. Two components call this hook independently (the overlay and the maximized view), so
 * each owns its own channel and its own state ref. That is harmless only while `sizes` is the
 * single key — every send already builds the complete map, so a full-state send is identical to
 * the old delta. Add a second key and the two instances will clobber each other's field on every
 * send; hoist to one shared instance first.
 */
function useEnergyLabelSync(isHost: boolean, sessionId: string | undefined, isRemote: boolean) {
  return useExperienceSync<IEnergyLabelSyncState>({
    sessionId: isRemote ? undefined : sessionId,
    topicId: 'energyLabel',
    initialState: { sizes: {} },
    schema: energyLabelSyncSchema,
    isHost,
  });
}

function getEffectiveSize(
  vehicleId: string,
  isHost: boolean,
  isRemote: boolean,
  syncSizes: Record<string, TEnergyLabelSize>
): TEnergyLabelSize {
  if (isHost || isRemote) {
    return energyLabelState().getSize(vehicleId);
  }
  // Non-remote customer follows sync state
  return syncSizes[vehicleId] ?? energyLabelState().getSize(vehicleId);
}

function canControlLabel(isHost: boolean, isRemote: boolean): boolean {
  return isHost || isRemote;
}

export const EnergyLabelOverlay: React.FC<IEnergyLabelProps> = ({ vehicleId, imageUrl }) => {
  const { _ } = useLingui();
  const { isVisible, isHost, session } = useEnergyLabelGating(vehicleId, imageUrl);
  const isRemote = session.sharingType === 'remote';
  const { state: syncState, send: sendSync } = useEnergyLabelSync(isHost, session.id, isRemote);

  const effectiveSize = getEffectiveSize(vehicleId, isHost, isRemote, syncState.sizes);
  const hasControl = canControlLabel(isHost, isRemote);

  const vehicles = userSession().get('vehicles') as Record<string, IVehicle> | undefined;
  const vehicle = vehicles?.[vehicleId];
  const vehicleIdentifier = vehicle?.carDetails?.title || vehicle?.carDisplayName || vehicle?.vinOrMarketingCode || '';

  const handleMinimize = () => {
    energyLabelState().setSize(vehicleId, 'minimized');
    if (isHost && !isRemote) {
      const sizes: Record<string, TEnergyLabelSize> = { ...syncState.sizes, [vehicleId]: 'minimized' };
      sendSync({ sizes });
    }
  };

  if (!isVisible || effectiveSize !== 'maximized') return null;

  return (
    <div
      className="ex3d-energy-label-overlay absolute bottom-[5.5rem] left-4 z-[100] flex flex-col items-start gap-1 rounded-lg p-2"
      data-testid="energy-label-maximized"
    >
      <div className="flex items-center justify-between gap-2" data-testid="energy-label-header">
        <span className="text-xs font-medium text-black">{vehicleIdentifier}</span>
        {hasControl && (
          <button
            type="button"
            onClick={handleMinimize}
            className="ex3d-button--minimize"
            aria-label={_(t`Minimize energy label`)}
            data-testid="energy-label-minimize-btn"
          >
            <MinimizeIcon />
          </button>
        )}
      </div>
      <img
        src={imageUrl!}
        alt={_(t`Energy efficiency label`)}
        className="h-48 w-auto rounded max-sm:h-32 landscape:h-[min(12rem,50vh)]"
      />
    </div>
  );
};

export const EnergyLabel: React.FC<IEnergyLabelProps> = ({ vehicleId, imageUrl }) => {
  const { _ } = useLingui();
  const { isVisible, isHost, session } = useEnergyLabelGating(vehicleId, imageUrl);
  const isRemote = session.sharingType === 'remote';
  const { state: syncState, send: sendSync } = useEnergyLabelSync(isHost, session.id, isRemote);

  const effectiveSize = getEffectiveSize(vehicleId, isHost, isRemote, syncState.sizes);
  const hasControl = canControlLabel(isHost, isRemote);

  const handleMaximize = () => {
    energyLabelState().setSize(vehicleId, 'maximized');
    if (isHost && !isRemote) {
      const sizes: Record<string, TEnergyLabelSize> = { ...syncState.sizes, [vehicleId]: 'maximized' };
      sendSync({ sizes });
    }
  };

  if (!isVisible || effectiveSize !== 'minimized') return null;

  const thumbnail = (
    <img
      src={imageUrl!}
      alt={_(t`Energy efficiency label`)}
      className="h-8 w-auto object-contain"
      data-testid="energy-label-thumbnail"
    />
  );

  if (!hasControl) {
    return (
      <div className="mr-1 flex h-full shrink-0 items-center" data-testid="energy-label-minimized">
        {thumbnail}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="energy-label-minimized"
      className="mr-1 flex h-full shrink-0 cursor-pointer items-center"
      onClick={handleMaximize}
      aria-label={_(t`Show energy label`)}
    >
      {thumbnail}
    </button>
  );
};
